import type { AddressDetails } from '@/shared/ui/address/AddressDetailsForm'

import type { AtHomeAddressResult, ManualAddress, SavedAddress } from '../model/types'

/** Human-readable single-line description for a manually entered address. */
export function buildManualDescription({ street, city, postalCode, country }: ManualAddress): string {
  return [street, [postalCode, city].filter(Boolean).join(' '), country].filter(Boolean).join(', ')
}

/**
 * Build the payload to persist from a confirmed search/saved selection.
 *
 * Prefers the structured components resolved by geocoding; only parses the
 * description as a last resort (e.g. geocoding failed). The description parse
 * assumes a "<postalCode> <city>" middle segment, which Places predictions
 * frequently don't have.
 */
export function buildSavedAddressFromSelection(params: {
  selectedAddress: AtHomeAddressResult
  details: AddressDetails
  label: string
  savedCount: number
}): Omit<SavedAddress, '_id'> {
  const { selectedAddress, details, label, savedCount } = params

  const parts = selectedAddress.description.split(',')
  const parsedMiddle = parts[1]?.trim()?.split(' ') ?? []

  return {
    label,
    street: selectedAddress.street || parts[0]?.trim() || selectedAddress.description,
    city: selectedAddress.city || parsedMiddle.slice(1).join(' ') || parsedMiddle.join(' ') || '',
    postalCode: selectedAddress.postalCode || (parsedMiddle.length > 1 ? parsedMiddle[0] : '') || '',
    country: selectedAddress.country || parts[2]?.trim() || '',
    apartment: details.apartment,
    floor: details.floor,
    doorCode: details.doorCode,
    instructions: details.instructions,
    placeId: selectedAddress.placeId,
    coordinates: selectedAddress.coordinates,
    isDefault: savedCount === 0,
  }
}

/** Build the payload to persist from a manually entered address. */
export function buildSavedAddressFromManual(params: {
  manual: ManualAddress
  details: AddressDetails
  label: string
  savedCount: number
}): Omit<SavedAddress, '_id'> {
  const { manual, details, label, savedCount } = params

  return {
    label,
    street: manual.street,
    city: manual.city,
    postalCode: manual.postalCode || '',
    country: manual.country || '',
    apartment: details.apartment,
    floor: details.floor,
    doorCode: details.doorCode,
    instructions: details.instructions,
    placeId: '',
    isDefault: savedCount === 0,
  }
}
